/**
 * Vergleich zwischen der API der Firmware und der der Oberflaeche (W03).
 *
 * Firmware, API-Hilfe und Beispiele tragen dieselbe API-Version (W02). Weichen
 * sie voneinander ab, passt die Hilfe nicht mehr zu dem, was auf dem Board
 * laeuft: ein Beispiel aus der Referenz kann dann mit einem Fehler abbrechen,
 * dessen Ursache im Programm gar nicht zu sehen ist. Deshalb wird das vor dem
 * Uebertragen gemeldet und nicht erst im Traceback.
 */

import { EXPECTED_API_VERSION } from "./api-version";
import type { RobotDeviceInfo } from "./connection";

export type CompatibilityLevel = "ok" | "minor" | "major" | "missing";

export interface CompatibilityResult {
  level: CompatibilityLevel;
  /** Version auf dem Board, soweit bekannt. */
  deviceVersion?: string;
  /** Version, die diese Oberflaeche erwartet. */
  expectedVersion: string;
}

interface Parsed {
  major: number;
  minor: number;
  patch: number;
}

/**
 * Zerlegt "1.2.3". Fehlende oder unlesbare Teile gelten als 0, damit ein
 * ungewohntes Format nicht zu einem harten Fehler fuehrt.
 */
function parse(version: string): Parsed | undefined {
  const match = /^(\d+)\.(\d+)(?:\.(\d+))?/.exec(version.trim());
  if (!match) {
    return undefined;
  }
  return {
    major: Number(match[1]),
    minor: Number(match[2]),
    patch: match[3] === undefined ? 0 : Number(match[3]),
  };
}

/**
 * Vergleicht die API der Firmware mit der erwarteten.
 *
 * * ``missing`` - kein Roboter erkannt, etwa ein Board ohne die Firmware.
 * * ``major``   - erste Stelle verschieden: Aufrufe koennen fehlen oder
 *                 anders heissen. Das ist ein Hindernis, kein Hinweis.
 * * ``minor``   - zweite Stelle verschieden: die Firmware kann weniger oder
 *                 mehr koennen als die Hilfe beschreibt.
 * * ``ok``      - gleiche Haupt- und Nebenversion. Die dritte Stelle bleibt
 *                 ohne Folgen; dort aendert sich nichts an der Schnittstelle.
 */
export function checkCompatibility(
  info: RobotDeviceInfo | undefined,
  expected: string = EXPECTED_API_VERSION
): CompatibilityResult {
  if (!info) {
    return { level: "missing", expectedVersion: expected };
  }
  const board = parse(info.api);
  const editor = parse(expected);
  if (!board || !editor) {
    // Ein unlesbares Format ist selbst schon ein Konflikt: die Herkunft der
    // Firmware laesst sich dann nicht bestaetigen.
    return {
      level: "major",
      deviceVersion: info.api,
      expectedVersion: expected,
    };
  }
  const result = {
    deviceVersion: info.api,
    expectedVersion: expected,
  };
  if (board.major !== editor.major) {
    return { ...result, level: "major" };
  }
  if (board.minor !== editor.minor) {
    return { ...result, level: "minor" };
  }
  return { ...result, level: "ok" };
}
